import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  Paper,
  Alert,
  Snackbar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Fab
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Send as SendIcon,
  ContactPhone as ContactPhoneIcon,
  Home as HomeIcon,
  Menu as MenuIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  MedicalServices as MedicalIcon,
  Instagram as InstagramIcon,
  Storefront as PetshopIcon
} from '@mui/icons-material';

const Contactos = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    mascota: '', // Usaremos este campo para "empresa"
    servicio: '',
    mensaje: ''
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.mobile-menu')) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validación básica
    if (!formData.nombre || !formData.email || !formData.servicio || !formData.mensaje) {
      setAlertMessage('Por favor completa todos los campos obligatorios');
      setOpenSnackbar(true);
      return;
    }

    // Crear mensaje para WhatsApp
    const mensaje = `¡Hola Belkysveterinaria! 
    
*Información de contacto:*
👤 Nombre: ${formData.nombre}
📧 Email: ${formData.email}
${formData.telefono ? `📱 Teléfono: ${formData.telefono}` : ''}
${formData.mascota ? `🏢 Empresa/Organización: ${formData.mascota}` : ''}

*Asunto:* ${formData.servicio}

*Mensaje:*
${formData.mensaje}

¡Espero su respuesta!`;

    const whatsappUrl = `https://wa.me/573216103829?text=${encodeURIComponent(mensaje)}`;
    window.open(whatsappUrl, '_blank');
    
    setAlertMessage('Formulario enviado correctamente. Te hemos redirigido a WhatsApp.');
    setOpenSnackbar(true);
    
    // Limpiar formulario
    setFormData({
      nombre: '',
      telefono: '',
      email: '',
      mascota: '',
      servicio: '',
      mensaje: ''
    });
  };

  const metodosContacto = [
    {
      titulo: 'WhatsApp',
      descripcion: 'Contacto directo y rápido',
      icono: <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366' }} />,
      info: '@belkys.veterinaria',
      action: () => window.open('https://wa.me/573216103829?text=Hola,%20me%20gustaría%20agendar%20una%20cita', '_blank'),
      buttonText: 'Enviar WhatsApp',
      buttonColor: '#25D366'
    },
    {
      titulo: 'Teléfono',
      descripcion: 'Llamada directa',
      icono: <PhoneIcon sx={{ fontSize: 40, color: '#c46494' }} />,
      info: '321 610 3829',
      action: () => window.open('tel:+573216103829', '_self'),
      buttonText: 'Llamar Ahora',
      buttonColor: '#c46494'
    },
    {
      titulo: 'Email',
      descripcion: 'Correo electrónico',
      icono: <EmailIcon sx={{ fontSize: 40, color: '#602442' }} />,
      info: 'belkys.veterinaria@gmail.com',
      action: () => window.open('mailto:belkys.veterinaria@gmail.com?subject=Consulta sobre servicios veterinarios', '_self'),
      buttonText: 'Enviar Email',
      buttonColor: '#602442'
    }
  ];

  const informacionAdicional = [
    {
      titulo: 'Horarios de Atención',
      icono: <TimeIcon sx={{ fontSize: 30, color: '#c46494' }} />,
      info: [
        'Lunes a Viernes: 8:00 AM - 6:00 PM',
        'Sábados: 8:00 AM - 2:00 PM'
      ]
    },
    {
      titulo: 'Zona de Cobertura',
      icono: <LocationIcon sx={{ fontSize: 30, color: '#c46494' }} />,
      info: [
        'Todo el área metropolitana',
        'Zona urbana y rural',
        'Disponibilidad según ubicación'
      ]
    }
  ];

  const serviciosDisponibles = [
    'Consulta General',
    'Vacunación',
    'Desparasitación',
    'Control de Crecimiento',
    'Esterilización/Castración',
    'Atención de Emergencias',
    'Análisis Clínicos',
    'Cirugías Menores',
    'Terapias y Rehabilitación',
    'Cuidado Geriátrico',
    'Medicina Preventiva',
    'Nutrición y Dietas',
    'Comportamiento Animal',
    'Cuidado Dental',
    'Grooming Básico',
    'Otro'
  ];

  return (
    <Box>
      {/* Header/Navbar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: scrolled ? '#fcdcf4' : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          boxShadow: scrolled ? '0 2px 10px rgba(0,0,0,0.1)' : 'none',
          zIndex: 1100,
          transition: 'all 0.3s ease'
        }}
      >
        <Toolbar sx={{ py: { xs: 0.5, sm: 1 }, px: { xs: 1, sm: 2 } }}>
          <Box 
            sx={{ 
              mr: { xs: 1, sm: 2, md: 4 }, 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer',
              flexShrink: 0
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src="/logo.png" 
              alt="Belkysvet Logo" 
              style={{ 
                height: window.innerWidth < 600 ? '40px' : window.innerWidth < 900 ? '50px' : '60px', 
                width: 'auto',
                maxWidth: window.innerWidth < 600 ? '100px' : '200px',
                marginRight: window.innerWidth < 600 ? '4px' : '8px'
              }} 
            />
            <Typography 
              variant="h5" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { xs: '0.8rem', sm: '1rem', md: '1.2rem' },
                display: { xs: 'none', sm: 'block' },
                textShadow: scrolled ? 'none' : '2px 2px 4px rgba(0,0,0,0.8)',
                transition: 'all 0.3s ease'
              }}
            >
              belkysveterinaria
            </Typography>
          </Box>
          
          {/* Spacer para empujar los botones a la derecha en móvil */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Menú de navegación para desktop */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            gap: { md: 2, lg: 4 },
            mr: 2
          }}>
            <Button 
              color="inherit" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { md: '1rem', lg: '1.1rem' },
                '&:hover': { 
                  backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.2)' 
                },
                px: { md: 1, lg: 2 },
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.8)',
                transition: 'all 0.3s ease'
              }}
              onClick={() => navigate('/')}
            >
              Inicio
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { md: '1rem', lg: '1.1rem' },
                '&:hover': { 
                  backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.2)' 
                },
                px: { md: 1, lg: 2 },
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.8)',
                transition: 'all 0.3s ease'
              }}
              onClick={() => navigate('/servicios')}
            >
              Servicios
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { md: '1rem', lg: '1.1rem' },
                '&:hover': { 
                  backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.2)' 
                },
                px: { md: 1, lg: 2 },
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.8)',
                transition: 'all 0.3s ease'
              }}
              onClick={() => navigate('/nosotros')}
            >
              Nosotros
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { md: '1rem', lg: '1.1rem' },
                '&:hover': { 
                  backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.2)' 
                },
                px: { md: 1, lg: 2 },
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.8)',
                transition: 'all 0.3s ease'
              }}
              onClick={() => navigate('/petshop')}
            >
              Petshop
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { md: '1rem', lg: '1.1rem' },
                backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                px: { md: 1, lg: 2 },
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.8)',
                transition: 'all 0.3s ease'
              }}
            >
              Contacto
            </Button>
          </Box>

          {/* Botón de menú móvil */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              sx={{ 
                color: scrolled ? '#602442' : 'white',
                '&:hover': { 
                  backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.2)' 
                },
                transition: 'all 0.3s ease'
              }}
            >
              {mobileMenuOpen ? <ExpandLessIcon /> : <MenuIcon />}
            </IconButton>
          </Box>
        </Toolbar>

        {/* Menú móvil desplegable */}
        <Collapse in={mobileMenuOpen}>
          <Box sx={{ 
            backgroundColor: 'rgba(252, 220, 244, 0.98)',
            borderTop: '1px solid rgba(96, 36, 66, 0.1)',
            py: 1
          }}>
            <Button 
              fullWidth
              color="inherit" 
              sx={{ 
                color: '#602442', 
                fontWeight: 'bold',
                justifyContent: 'flex-start',
                px: 3,
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(96, 36, 66, 0.1)' }
              }}
              onClick={() => {
                navigate('/');
                setMobileMenuOpen(false);
              }}
            >
              <HomeIcon sx={{ mr: 2 }} />
              Inicio
            </Button>
            <Button 
              fullWidth
              color="inherit" 
              sx={{ 
                color: '#602442', 
                fontWeight: 'bold',
                justifyContent: 'flex-start',
                px: 3,
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(96, 36, 66, 0.1)' }
              }}
              onClick={() => {
                navigate('/servicios');
                setMobileMenuOpen(false);
              }}
            >
              <MedicalIcon sx={{ mr: 2 }} />
              Servicios
            </Button>
            <Button 
              fullWidth
              color="inherit" 
              sx={{ 
                color: '#602442', 
                fontWeight: 'bold',
                justifyContent: 'flex-start',
                px: 3,
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(96, 36, 66, 0.1)' }
              }}
              onClick={() => {
                navigate('/nosotros');
                setMobileMenuOpen(false);
              }}
            >
              <InfoIcon sx={{ mr: 2 }} />
              Nosotros
            </Button>
            <Button 
              fullWidth
              color="inherit" 
              sx={{ 
                color: '#602442', 
                fontWeight: 'bold',
                justifyContent: 'flex-start',
                px: 3,
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(96, 36, 66, 0.1)' }
              }}
              onClick={() => {
                navigate('/petshop');
                setMobileMenuOpen(false);
              }}
            >
              <PetshopIcon sx={{ mr: 2 }} />
              Petshop
            </Button>
            <Button 
              fullWidth
              color="inherit" 
              sx={{ 
                color: '#602442', 
                fontWeight: 'bold',
                justifyContent: 'flex-start',
                px: 3,
                py: 1.5,
                backgroundColor: 'rgba(96, 36, 66, 0.1)'
              }}
            >
              <ContactPhoneIcon sx={{ mr: 2 }} />
              Contacto
            </Button>
          </Box>
        </Collapse>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: {
            xs: 'linear-gradient(rgba(196, 100, 148, 0.8), rgba(96, 36, 66, 0.8)), url("/contacto.jpg")',
            md: 'linear-gradient(rgba(196, 100, 148, 0.7), rgba(96, 36, 66, 0.7)), url("/contacto.jpg")'
          },
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          color: 'white',
          py: { xs: 6, sm: 8, md: 10 },
          px: { xs: 2, sm: 3 },
          textAlign: 'center',
          minHeight: { xs: '50vh', sm: '60vh', md: '60vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: { xs: '80px', sm: '90px', md: '100px' }
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            fontWeight="bold"
            sx={{ 
              textShadow: '3px 3px 6px rgba(0,0,0,0.8)',
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.5rem' },
              lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
              mb: { xs: 2, sm: 3, md: 4 }
            }}
          >
            Contáctanos
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: { xs: 3, sm: 4, md: 4 }, 
              opacity: 0.95,
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
              fontSize: { xs: '1rem', sm: '1.3rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
              px: { xs: 1, sm: 2 }
            }}
          >
            Estamos aquí para cuidar a tu mascota. Comunícate con nosotros de la manera que prefieras
          </Typography>
        </Container>
      </Box>

      {/* Métodos de Contacto */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="h3" 
          align="center" 
          gutterBottom 
          sx={{ 
            color: '#602442',
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
            mb: { xs: 2, sm: 3, md: 4 }
          }} 
          fontWeight="bold"
        >
          Formas de Contacto
        </Typography>
        <Typography 
          variant="h6" 
          align="center" 
          sx={{ 
            color: '#a55e88', 
            mb: { xs: 4, sm: 5, md: 6 },
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
            px: { xs: 1, sm: 2 }
          }}
        >
          Elige la opción que más te convenga
        </Typography>
        
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
          {metodosContacto.map((metodo, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
              <Card 
                sx={{ 
                  height: { xs: 'auto', sm: '100%' },
                  minHeight: { xs: '300px', sm: '350px', md: '400px' },
                  width: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': { 
                    transform: { xs: 'none', sm: 'translateY(-5px)' },
                    boxShadow: { xs: 3, sm: 6 }
                  },
                  border: '2px solid #fcdcf4',
                  maxWidth: '350px',
                  mx: 'auto'
                }}
              >
                <CardContent sx={{ 
                  p: { xs: 2, sm: 3, md: 4 }, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <Box>
                    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                      {metodo.icono}
                    </Box>
                    <Typography 
                      variant="h4" 
                      gutterBottom 
                      fontWeight="bold" 
                      sx={{ 
                        color: '#602442', 
                        mb: { xs: 1, sm: 2 },
                        fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.125rem' },
                        minHeight: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {metodo.titulo}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#a55e88', 
                        mb: { xs: 2, sm: 3 },
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        minHeight: { xs: '1.5rem', sm: '2rem' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {metodo.descripcion}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#602442', 
                        mb: { xs: 3, sm: 4 }, 
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                        minHeight: { xs: '1.5rem', sm: '2rem' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {metodo.info}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 'auto' }}>
                    <Button
                      variant="contained"
                      onClick={metodo.action}
                      sx={{ 
                        backgroundColor: metodo.buttonColor,
                        '&:hover': { 
                          backgroundColor: metodo.buttonColor,
                          filter: 'brightness(0.9)'
                        },
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1.5 },
                        borderRadius: 3,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        fontWeight: 'bold',
                        width: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      {metodo.buttonText}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Información Adicional */}
      <Box sx={{ backgroundColor: '#c9aebc', py: { xs: 4, sm: 6, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom 
            sx={{ 
              color: '#602442',
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
              mb: { xs: 2, sm: 3, md: 4 }
            }} 
            fontWeight="bold"
          >
            Información Útil
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ 
              color: '#602442', 
              mb: { xs: 4, sm: 5, md: 6 },
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              px: { xs: 1, sm: 2 }
            }}
          >
            Todo lo que necesitas saber sobre nuestros servicios
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
            {informacionAdicional.map((info, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                <Card 
                  sx={{ 
                    height: { xs: 'auto', sm: '100%' },
                    minHeight: { xs: '280px', sm: '320px', md: '360px' },
                    width: '100%',
                    textAlign: 'center',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': { 
                      transform: { xs: 'none', sm: 'translateY(-5px)' },
                      boxShadow: { xs: 3, sm: 6 }
                    },
                    backgroundColor: 'white',
                    maxWidth: '350px',
                    mx: 'auto'
                  }}
                >
                  <CardContent sx={{ 
                    p: { xs: 2, sm: 3, md: 4 }, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <Box>
                      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                        {info.icono}
                      </Box>
                      <Typography 
                        variant="h5" 
                        gutterBottom 
                        fontWeight="bold" 
                        sx={{ 
                          color: '#602442', 
                          mb: { xs: 2, sm: 3 },
                          fontSize: { xs: '1.3rem', sm: '1.5rem' },
                          minHeight: { xs: '2.5rem', sm: '3rem' },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {info.titulo}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      {info.info.map((item, itemIndex) => (
                        <Typography 
                          key={itemIndex}
                          variant="body2" 
                          sx={{ 
                            color: '#602442', 
                            mb: { xs: 0.5, sm: 1 },
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            textAlign: 'center'
                          }}
                        >
                          • {item}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Formulario de Contacto */}
      <Box sx={{ backgroundColor: '#fcdcf4', py: { xs: 6, sm: 8, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            align="center" 
            gutterBottom 
            sx={{ 
              color: '#602442', 
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.5rem' }
            }} 
            fontWeight="bold"
          >
            Envíanos un mensaje
          </Typography>
          <Typography 
            variant="h5" 
            align="center" 
            sx={{ 
              color: '#a55e88', 
              mb: { xs: 4, sm: 6, md: 8 },
              fontSize: { xs: '1rem', sm: '1.3rem', md: '1.5rem' },
              px: { xs: 1, sm: 2 }
            }}
          >
            Completa el formulario y nos pondremos en contacto contigo rápidamente
          </Typography>
          
          <Paper sx={{ 
            p: { xs: 3, sm: 6, md: 8 }, 
            backgroundColor: 'white', 
            borderRadius: { xs: 4, md: 6 }, 
            boxShadow: { xs: 4, md: 8 }, 
            border: '3px solid #c46494',
            maxWidth: '800px',
            mx: 'auto'
          }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={{ xs: 3, sm: 4, md: 5 }}>
                
                {/* Título del formulario */}
                <Grid item xs={12}>
                  <Typography variant="h4" sx={{ 
                    color: '#602442', 
                    fontWeight: 'bold', 
                    mb: { xs: 2, sm: 3, md: 4 },
                    textAlign: 'center',
                    borderBottom: '2px solid #c46494',
                    pb: { xs: 1, sm: 2 },
                    fontSize: { xs: '1.3rem', sm: '1.8rem', md: '2.125rem' }
                  }}>
                    📋 Información de Contacto
                  </Typography>
                </Grid>

                {/* Nombre */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ 
                    color: '#602442', 
                    fontWeight: 'bold', 
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textAlign: 'left'
                  }}>
                    👤 Nombre completo
                  </Typography>
                  <TextField
                    fullWidth
                    label="Nombre completo *"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="Ej: María José García López"
                    size={window.innerWidth < 600 ? "medium" : "large"}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        '&.Mui-focused': {
                          color: '#c46494',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ 
                    color: '#602442', 
                    fontWeight: 'bold', 
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textAlign: 'left'
                  }}>
                    📧 Correo electrónico
                  </Typography>
                  <TextField
                    fullWidth
                    label="Email *"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="Ej: maria.garcia@email.com"
                    size={window.innerWidth < 600 ? "medium" : "large"}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        '&.Mui-focused': {
                          color: '#c46494',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Teléfono */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ 
                    color: '#602442', 
                    fontWeight: 'bold', 
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textAlign: 'left'
                  }}>
                    📱 Teléfono (opcional)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="Ej: +57 321 610 3829"
                    size={window.innerWidth < 600 ? "medium" : "large"}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        '&.Mui-focused': {
                          color: '#c46494',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Empresa/Organización */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ 
                    color: '#602442', 
                    fontWeight: 'bold', 
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textAlign: 'left'
                  }}>
                    🏢 Empresa (opcional)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Empresa/Organización"
                    name="mascota"
                    value={formData.mascota}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="Ej: Fundación Amigos de los Animales"
                    size={window.innerWidth < 600 ? "medium" : "large"}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        '&.Mui-focused': {
                          color: '#c46494',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Servicio */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ 
                    color: '#602442', 
                    fontWeight: 'bold', 
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textAlign: 'left'
                  }}>
                    🩺 Tipo de servicio
                  </Typography>
                  <FormControl fullWidth size={window.innerWidth < 600 ? "medium" : "large"}>
                    <InputLabel 
                      sx={{ 
                        '&.Mui-focused': { color: '#c46494' },
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }}
                    >
                      Selecciona el servicio *
                    </InputLabel>
                    <Select
                      name="servicio"
                      value={formData.servicio}
                      onChange={handleInputChange}
                      label="Selecciona el servicio *"
                      sx={{
                        borderRadius: 3,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                      }}
                    >
                      {serviciosDisponibles.map((servicio) => (
                        <MenuItem 
                          key={servicio} 
                          value={servicio}
                          sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                        >
                          {servicio}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Mensaje */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ 
                    color: '#602442', 
                    fontWeight: 'bold', 
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textAlign: 'left'
                  }}>
                    💬 Mensaje
                  </Typography>
                  <TextField
                    fullWidth
                    label="Cuéntanos más detalles *"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleInputChange}
                    variant="outlined"
                    multiline
                    rows={window.innerWidth < 600 ? 4 : 6}
                    placeholder="Describe tu consulta, síntomas de tu mascota, horario preferido, ubicación, etc."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#c46494',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        '&.Mui-focused': {
                          color: '#c46494',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Botón de envío */}
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', mt: { xs: 2, sm: 3 } }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<SendIcon />}
                      sx={{
                        backgroundColor: '#602442',
                        '&:hover': { 
                          backgroundColor: '#a55e88',
                          transform: 'translateY(-2px)',
                          boxShadow: 6
                        },
                        px: { xs: 4, sm: 6 },
                        py: { xs: 1.5, sm: 2 },
                        borderRadius: 3,
                        fontSize: { xs: '1rem', sm: '1.2rem' },
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease',
                        width: { xs: '100%', sm: 'auto' },
                        maxWidth: { xs: '300px', sm: 'none' }
                      }}
                    >
                      Enviar Mensaje
                    </Button>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block', 
                        color: '#a55e88', 
                        mt: { xs: 1, sm: 2 },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                      }}
                    >
                      * Campos obligatorios. Te redirigiremos a WhatsApp para completar el contacto.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </Box>

      {/* Footer simplificado */}
      <Box sx={{ backgroundColor: '#602442', color: 'white', py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: { xs: 1, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <img 
                  src="/logo192.png" 
                  alt="Belkysvet Logo" 
                  style={{ 
                    height: window.innerWidth < 600 ? '60px' : '80px', 
                    width: 'auto',
                    marginRight: window.innerWidth < 600 ? '0' : '20px',
                    marginBottom: window.innerWidth < 600 ? '10px' : '0'
                  }} 
                />
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'center',
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#c46494', 
                      mb: 1, 
                      fontWeight: 'bold',
                      fontSize: { xs: '1.2rem', sm: '1.25rem' }
                    }}
                  >
                    Belkysveterinaria
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 0, 
                      lineHeight: 1.4,
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                  >
                    Atención veterinaria profesional en la comodidad de tu hogar
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mt: 1.5,
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}>
                    <LocationIcon sx={{ 
                      fontSize: { xs: '1rem', sm: '1.1rem' }, 
                      color: '#c46494',
                      mr: 0.5 
                    }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.85rem', sm: '0.875rem' },
                        color: '#fcdcf4',
                        fontWeight: '500'
                      }}
                    >
                      Servicio a domicilio - Popayán, Cauca
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: { xs: 'center', md: 'flex-end' },
                justifyContent: 'center',
                height: '100%',
                textAlign: { xs: 'center', md: 'right' }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1,
                  justifyContent: { xs: 'center', md: 'flex-end' }
                }}>
                  <IconButton
                    component="a"
                    href="https://wa.me/573216103829?text=Hola,%20me%20gustaría%20agendar%20una%20cita"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#c46494',
                      '&:hover': {
                        color: '#fcdcf4',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.3s ease',
                      p: 1
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.3rem' } }} />
                  </IconButton>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1rem' }, 
                      fontWeight: '500',
                      ml: 1
                    }}
                  >
                    @belkys.veterinaria
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'flex-end' },
                  mb: 1
                }}>
                  <IconButton
                    component="a"
                    href="mailto:belkys.veterinaria@gmail.com"
                    sx={{
                      color: '#c46494',
                      '&:hover': {
                        color: '#fcdcf4',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.3s ease',
                      p: 1
                    }}
                  >
                    <EmailIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.3rem' } }} />
                  </IconButton>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1rem' }, 
                      fontWeight: '500',
                      ml: 1
                    }}
                  >
                    belkys.veterinaria@gmail.com
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'flex-end' }
                }}>
                  <IconButton
                    component="a"
                    href="https://www.instagram.com/belkys.veterinaria/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#c46494',
                      '&:hover': {
                        color: '#fcdcf4',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.3s ease',
                      p: 1
                    }}
                  >
                    <InstagramIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem' } }} />
                  </IconButton>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1rem' }, 
                      fontWeight: '500',
                      ml: 1
                    }}
                  >
                    @belkys.veterinaria
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ 
            textAlign: 'center', 
            mt: { xs: 2, sm: 3 }, 
            pt: { xs: 2, sm: 3 }, 
            borderTop: '1px solid rgba(255,255,255,0.2)' 
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                opacity: 0.8,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              © 2025 Belkysveterinaria. Todos los derechos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity={alertMessage.includes('correctamente') ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Botón flotante de WhatsApp */}
      <Fab
        color="success"
        aria-label="whatsapp"
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 20 },
          right: { xs: 16, sm: 20 },
          backgroundColor: '#c46494',
          '&:hover': { backgroundColor: '#a55e88' },
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          zIndex: 1000
        }}
        href="https://wa.me/573216103829?text=Hola,%20me%20gustaría%20agendar%20una%20cita"
        target="_blank"
      >
        <WhatsAppIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
      </Fab>
    </Box>
  );
};

export default Contactos;
